// ---------------------------------------------------------------------------
// SocialIcons Renderer
// ---------------------------------------------------------------------------
// Engagement primitive — publishes the editor `SocialIcons` block as a
// row of social media links parsed from the pipe-delimited textarea the
// Puck editor uses ("Platform | URL" per line). Every URL is passed
// through the renderer's `url()` safelist so only http(s)/mailto/tel
// schemes survive — `javascript:` URIs are coerced to a safe `#`
// fallback. The platform label is rendered as escaped text so authors
// can localise it without injecting markup.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface SocialIconsProps {
  icons?: string;
  size?: string;
  style?: string;
}

interface SocialLink {
  platform: string;
  href: string;
}

const ALLOWED_SIZES = new Set(["sm", "md", "lg"]);
const ALLOWED_STYLES = new Set(["filled", "outline", "plain"]);

function parseIcons(value: unknown): SocialLink[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [platformPart, urlPart] = line.split("|");
      return {
        platform: (platformPart ?? "").trim(),
        href: (urlPart ?? "").trim(),
      };
    })
    .filter((link) => link.platform.length > 0 && link.href.length > 0);
}

function normalizeOption(value: unknown, allowed: Set<string>, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return allowed.has(trimmed) ? trimmed : fallback;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as SocialIconsProps;

  const links = parseIcons(p.icons);
  if (links.length === 0) {
    return "<!-- social-icons missing links -->";
  }

  const size = normalizeOption(p.size, ALLOWED_SIZES, "md");
  const styleVariant = normalizeOption(p.style, ALLOWED_STYLES, "filled");

  const items = links
    .map((link) => {
      const safeHref = url(link.href);
      return `      <li class="ease-social-icons__item"><a class="ease-social-icons__link" href="${safeHref}" rel="noopener noreferrer" aria-label="${attr(link.platform)}"><span class="ease-social-icons__label">${text(link.platform)}</span></a></li>`;
    })
    .join("\n");

  return `<section class="ease-section ease-social-icons" data-size="${attr(size)}" data-style="${attr(styleVariant)}">
    <ul class="ease-social-icons__list" role="list">
${items}
    </ul>
</section>`;
}

registerRenderer("social-icons", { render });
